import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import ConfirmModal from '../../components/ConfirmModal';

const RECIPE_PHOTOS_BUCKET = 'recipe-photos';

// Gemigreerde photo_url-rijen (vóór de foto-galerij-feature) bevatten een
// externe URL i.p.v. een pad in de recipe-photos-bucket; onderscheiden op
// basis van het http(s)-prefix (zie migratie 20260729100000).
function getRecipePhotoUrl(storagePath) {
  if (!storagePath) return null;
  if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) return storagePath;
  return supabase.storage.from(RECIPE_PHOTOS_BUCKET).getPublicUrl(storagePath).data.publicUrl;
}

function randomId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

// Voorkomt dat scrollen boven een gefocuste number-input de waarde wijzigt
// (browser-default: scrollwheel verhoogt/verlaagt een gefocust number-veld).
function blurOnWheel(e) {
  e.target.blur();
}

// Upload één foto naar de recipe-photos-bucket. `folderId` is recept.id bij
// een bestaand recept, of een losse sessie-id bij een nieuw recept (de RPC
// kent pas een echt recipe_id toe ná upload — het pad hoeft daar niet 1-op-1
// mee overeen te komen, alleen storage_path wordt opgeslagen).
async function uploadRecipePhoto(folderId, file) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const storagePath = `${folderId}/${randomId()}.${ext}`;
  const { error } = await supabase.storage.from(RECIPE_PHOTOS_BUCKET).upload(storagePath, file, { contentType: file.type });
  if (error) throw error;
  return storagePath;
}

// Cleanup van al geüploade bestanden — wees-bestanden voorkomen als de
// save_extended_recipe-RPC daarna faalt, of als een bestaande foto uit de
// lijst is verwijderd (storage valt buiten de SQL-transactie van de RPC).
async function deleteRecipePhotos(storagePaths) {
  const ownPaths = (storagePaths || []).filter(p => p && !p.startsWith('http'));
  if (ownPaths.length === 0) return;
  const { error } = await supabase.storage.from(RECIPE_PHOTOS_BUCKET).remove(ownPaths);
  if (error) throw error;
}

const MEAL_TYPES = [
  { value: 'breakfast',       label: 'Ontbijt' },
  { value: 'snack_morning',   label: 'Tussendoor (ochtend)' },
  { value: 'lunch',           label: 'Lunch' },
  { value: 'snack_afternoon', label: 'Tussendoor (middag)' },
  { value: 'dinner',          label: 'Avondeten' },
  { value: 'evening_snack',   label: 'Avondsnack' },
];

const DINNER_COMPONENT_TYPES = [
  { value: 'protein',    label: 'Vlees/Vis' },
  { value: 'carbs',      label: 'Koolhydraten' },
  { value: 'vegetables', label: 'Groente' },
];

const DIFFICULTIES = ['makkelijk', 'gemiddeld', 'moeilijk'];

function difficultyLabel(value) {
  if (!value) return '';
  return t(`recipes.difficulty${value.charAt(0).toUpperCase()}${value.slice(1)}`);
}

function SortableIngredientRow({ id, ing, index, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={{ ...style, display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ cursor: 'grab', touchAction: 'none' }}
        {...attributes}
        {...listeners}
        title={t('recipes.dragHandle')}
      >
        ⠿
      </button>
      <input className="form-input" style={{ flex: 2 }} placeholder={t('recipes.ingredientNamePlaceholder')} value={ing.naam} onChange={e => onUpdate(index, 'naam', e.target.value)} />
      <input className="form-input" style={{ flex: 1 }} type="number" step="0.1" placeholder={t('recipes.ingredientAmountPlaceholder')} value={ing.hoeveelheid} onChange={e => onUpdate(index, 'hoeveelheid', e.target.value)} onWheel={blurOnWheel} />
      <input className="form-input" style={{ flex: 1 }} placeholder={t('recipes.ingredientUnitPlaceholder')} value={ing.eenheid} onChange={e => onUpdate(index, 'eenheid', e.target.value)} />
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onRemove(index)}>✕</button>
    </div>
  );
}

function SortableStepRow({ id, s, index, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={{ ...style, border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ cursor: 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
            title={t('recipes.dragHandle')}
          >
            ⠿
          </button>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)' }}>{index + 1}</span>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onRemove(index)}>✕</button>
      </div>
      <input className="form-input" style={{ marginBottom: 6 }} placeholder={t('recipes.stepTitlePlaceholder')} value={s.titel} onChange={e => onUpdate(index, 'titel', e.target.value)} />
      <textarea className="form-input" rows={2} style={{ marginBottom: 6, resize: 'vertical' }} placeholder={t('recipes.stepInstructionPlaceholder')} value={s.instructie} onChange={e => onUpdate(index, 'instructie', e.target.value)} />
      <input className="form-input" type="number" min="0" placeholder={t('recipes.stepDurationPlaceholder')} value={s.tijdsduur_seconden} onChange={e => onUpdate(index, 'tijdsduur_seconden', e.target.value)} onWheel={blurOnWheel} />
    </div>
  );
}

export default function RecipeFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [loadingInit, setLoadingInit] = useState(!isNew);
  const [form, setForm] = useState({
    name: '', meal_moments: [], calories: '', protein: '', carbs: '', fat: '',
    description: '', is_dinner_component: false, dinner_component_type: '',
    recipe_type: 'basis', video_url: '', prep_time_minutes: '', cook_time_minutes: '',
    difficulty: 'makkelijk', servings: '',
  });
  const [ingredients, setIngredients] = useState([]);
  const [steps, setSteps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const originalPhotoPathsRef = useRef([]);
  const newlyUploadedPathsRef = useRef([]);
  const newRecipeFolderRef = useRef(randomId());
  const recipeIdRef = useRef(id || null);

  // Ingrediënten/stappen krijgen een stabiele client-side id voor dnd-kit
  // (de database kent pas een echt id toe ná opslaan, nieuwe rijen hebben er
  // nog geen).
  const nextRowIdRef = useRef(0);
  function newRowId() { nextRowIdRef.current += 1; return `row-${nextRowIdRef.current}`; }

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoadingInit(true);
      try {
        const { data: recept, error: err } = await supabase.from('recipes').select('*, recipe_meal_moments(meal_moment)').eq('id', id).maybeSingle();
        if (err) throw err;
        if (!recept) { setError(t('recipes.foutLaden') + t('recipes.geenRecepten')); return; }
        setForm({
          name: recept.name ?? '',
          meal_moments: (recept.recipe_meal_moments || []).map(m => m.meal_moment),
          calories: recept.calories ?? '',
          protein: recept.protein ?? '',
          carbs: recept.carbs ?? '',
          fat: recept.fat ?? '',
          description: recept.description ?? '',
          is_dinner_component: recept.is_dinner_component ?? false,
          dinner_component_type: recept.dinner_component_type ?? '',
          recipe_type: recept.recipe_type ?? 'basis',
          video_url: recept.video_url ?? '',
          prep_time_minutes: recept.prep_time_minutes ?? '',
          cook_time_minutes: recept.cook_time_minutes ?? '',
          difficulty: recept.difficulty ?? 'makkelijk',
          servings: recept.servings ?? '',
        });
        if (recept.recipe_type === 'uitgebreid') {
          const [{ data: ing }, { data: st }, { data: ph }] = await Promise.all([
            supabase.from('recipe_ingredients').select('*').eq('recipe_id', id).order('volgorde'),
            supabase.from('recipe_steps').select('*').eq('recipe_id', id).order('stap_nummer'),
            supabase.from('recipe_photos').select('*').eq('recipe_id', id).order('volgorde'),
          ]);
          setIngredients((ing || []).map(i => ({ _id: newRowId(), naam: i.naam, hoeveelheid: i.hoeveelheid ?? '', eenheid: i.eenheid ?? '' })));
          setSteps((st || []).map(s => ({ _id: newRowId(), titel: s.titel ?? '', instructie: s.instructie, tijdsduur_seconden: s.tijdsduur_seconden ?? '' })));
          const photoRows = (ph || []).map(p => ({ _id: newRowId(), storage_path: p.storage_path }));
          setPhotos(photoRows);
          originalPhotoPathsRef.current = photoRows.map(p => p.storage_path);
        }
      } catch (err) {
        setError(t('recipes.foutLaden') + err.message);
      } finally {
        setLoadingInit(false);
      }
    })();
  }, [id, isNew]);

  function markDirty() { setDirty(true); }

  useEffect(() => {
    function handleBeforeUnload(e) {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  function updateForm(patch) {
    setForm(p => ({ ...p, ...patch }));
    markDirty();
  }

  async function handlePickPhotos(e) {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0) return;
    setUploadingPhoto(true);
    try {
      const folderId = recipeIdRef.current || newRecipeFolderRef.current;
      for (const file of files) {
        const storagePath = await uploadRecipePhoto(folderId, file);
        newlyUploadedPathsRef.current.push(storagePath);
        setPhotos(prev => [...prev, { _id: newRowId(), storage_path: storagePath }]);
      }
      markDirty();
    } catch (err) {
      setError(t('recipes.photoUploadError'));
    } finally {
      setUploadingPhoto(false);
    }
  }

  function movePhoto(index, direction) {
    setPhotos(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    markDirty();
  }

  function removePhoto(index) {
    setPhotos(prev => {
      const removed = prev[index];
      if (newlyUploadedPathsRef.current.includes(removed.storage_path)) {
        deleteRecipePhotos([removed.storage_path]).catch(() => {});
        newlyUploadedPathsRef.current = newlyUploadedPathsRef.current.filter(p => p !== removed.storage_path);
      }
      return prev.filter((_, i) => i !== index);
    });
    markDirty();
  }

  // Nieuwe rij verschijnt onderaan de lijst en kan vervolgens gesleept
  // worden — lost het "vergeten-eerste-stap"-scenario op zonder alles te
  // hoeven verwijderen.
  function addIngredientRow() { setIngredients(prev => [...prev, { _id: newRowId(), naam: '', hoeveelheid: '', eenheid: '' }]); markDirty(); }
  function updateIngredientRow(i, field, value) { setIngredients(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row)); markDirty(); }
  function removeIngredientRow(i) { setIngredients(prev => prev.filter((_, idx) => idx !== i)); markDirty(); }

  function addStepRow() { setSteps(prev => [...prev, { _id: newRowId(), titel: '', instructie: '', tijdsduur_seconden: '' }]); markDirty(); }
  function updateStepRow(i, field, value) { setSteps(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row)); markDirty(); }
  function removeStepRow(i) { setSteps(prev => prev.filter((_, idx) => idx !== i)); markDirty(); }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleIngredientDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setIngredients(prev => {
      const oldIndex = prev.findIndex(i => i._id === active.id);
      const newIndex = prev.findIndex(i => i._id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
    markDirty();
  }

  function handleStepDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps(prev => {
      const oldIndex = prev.findIndex(s => s._id === active.id);
      const newIndex = prev.findIndex(s => s._id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
    markDirty();
  }

  function handleCancel() {
    if (dirty) { setShowCancelConfirm(true); return; }
    navigate('/coach/recepten');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError(t('recipes.valNaam')); return; }
    if (form.meal_moments.length === 0) { setError(t('recipes.valEetmoment')); return; }
    if (form.calories === '' || isNaN(Number(form.calories))) { setError(t('recipes.valKcal')); return; }
    if (form.recipe_type === 'uitgebreid' && !steps.some(s => s.instructie?.trim())) {
      setError(t('recipes.extendedRequiresStep'));
      return;
    }

    setSaving(true);
    try {
      // Alle recepten (ook basis) gaan via de RPC: de "≥1 eetmoment"-
      // deferred-constraint-trigger (migratie 20260729110000) kan alleen
      // recipes + recipe_meal_moments atomair samen zien binnen één
      // transactie — een losse insert/update op recipes zou meteen falen.
      const isUitgebreid = form.recipe_type === 'uitgebreid';
      const { data: userData } = await supabase.auth.getUser();
      const { error: err } = await supabase.rpc('save_extended_recipe', {
        p_recipe_id: recipeIdRef.current,
        p_name: form.name.trim(),
        p_description: form.description.trim() || null,
        p_meal_moments: form.meal_moments,
        p_calories: Number(form.calories),
        p_protein: form.protein !== '' ? Number(form.protein) : null,
        p_carbs: form.carbs !== '' ? Number(form.carbs) : null,
        p_fat: form.fat !== '' ? Number(form.fat) : null,
        p_is_dinner_component: form.is_dinner_component,
        p_dinner_component_type: form.is_dinner_component ? (form.dinner_component_type || null) : null,
        p_recipe_type: form.recipe_type,
        p_video_url: isUitgebreid ? (form.video_url.trim() || null) : null,
        p_prep_time_minutes: isUitgebreid && form.prep_time_minutes !== '' ? Number(form.prep_time_minutes) : null,
        p_cook_time_minutes: isUitgebreid && form.cook_time_minutes !== '' ? Number(form.cook_time_minutes) : null,
        p_difficulty: isUitgebreid ? form.difficulty : null,
        p_servings: isUitgebreid && form.servings !== '' ? Number(form.servings) : null,
        p_created_by: userData?.user?.id ?? null,
        p_ingredients: isUitgebreid ? ingredients.filter(i => i.naam?.trim()).map((i, idx) => ({
          naam: i.naam.trim(), hoeveelheid: i.hoeveelheid !== '' ? Number(i.hoeveelheid) : null, eenheid: i.eenheid?.trim() || null, volgorde: idx,
        })) : [],
        p_steps: isUitgebreid ? steps.filter(s => s.instructie?.trim()).map((s, idx) => ({
          stap_nummer: idx + 1, titel: s.titel?.trim() || null, instructie: s.instructie.trim(), tijdsduur_seconden: s.tijdsduur_seconden !== '' ? Number(s.tijdsduur_seconden) : null,
        })) : [],
        p_photos: isUitgebreid ? photos.map((p, idx) => ({ storage_path: p.storage_path, volgorde: idx })) : [],
      });
      if (err) throw err;

      if (isUitgebreid) {
        const keptPaths = photos.map(p => p.storage_path);
        const removedExisting = originalPhotoPathsRef.current.filter(p => !keptPaths.includes(p));
        if (removedExisting.length) deleteRecipePhotos(removedExisting).catch(() => {});
      }
      setDirty(false);
      navigate('/coach/recepten');
    } catch (err) {
      if (newlyUploadedPathsRef.current.length) {
        deleteRecipePhotos(newlyUploadedPathsRef.current).catch(() => {});
      }
      setError(t('recipes.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loadingInit) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost" onClick={handleCancel} style={{ marginBottom: 12 }}>
          {t('recipes.terug')}
        </button>
        <h1 className="page-title">
          {isNew ? t('recipes.modalTitelNieuw') : t('recipes.modalTitelBewerken')}
        </h1>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 16, padding: 20 }}>
          <div className="form-group">
            <label className="form-label">{t('recipes.labelNaam')}</label>
            <input
              className="form-input"
              value={form.name}
              onChange={e => updateForm({ name: e.target.value })}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('recipes.labelEetmoment')}</label>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {MEAL_TYPES.map(m => {
                const checked = form.meal_moments.includes(m.value);
                return (
                  <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...form.meal_moments, m.value]
                          : form.meal_moments.filter(v => v !== m.value);
                        updateForm({ meal_moments: next });
                      }}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    {m.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('recipes.labelKcal')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                value={form.calories}
                onChange={e => updateForm({ calories: e.target.value })}
                onWheel={blurOnWheel}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('recipes.labelEiwitten')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.1"
                placeholder={t('recipes.optioneel')}
                value={form.protein}
                onChange={e => updateForm({ protein: e.target.value })}
                onWheel={blurOnWheel}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('recipes.labelKoolhydraten')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.1"
                placeholder={t('recipes.optioneel')}
                value={form.carbs}
                onChange={e => updateForm({ carbs: e.target.value })}
                onWheel={blurOnWheel}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('recipes.labelVetten')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.1"
                placeholder={t('recipes.optioneel')}
                value={form.fat}
                onChange={e => updateForm({ fat: e.target.value })}
                onWheel={blurOnWheel}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">{t('recipes.labelBeschrijving')}</label>
            <textarea
              className="form-input"
              rows={3}
              value={form.description}
              onChange={e => updateForm({ description: e.target.value })}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="is_dinner_component"
              checked={form.is_dinner_component}
              onChange={e => updateForm({ is_dinner_component: e.target.checked })}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="is_dinner_component" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
              {t('recipes.labelAvondetenComponent')}
            </label>
          </div>

          {form.is_dinner_component && (
            <div className="form-group">
              <label className="form-label">{t('recipes.labelComponentType')}</label>
              <select
                className="form-input"
                value={form.dinner_component_type}
                onChange={e => updateForm({ dinner_component_type: e.target.value })}
              >
                <option value="">{t('recipes.kiesComponentType')}</option>
                {DINNER_COMPONENT_TYPES.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 8, marginBottom: 0 }}>
            <label className="form-label">{t('recipes.recipeTypeLabel')}</label>
            <select
              className="form-input"
              value={form.recipe_type}
              onChange={e => updateForm({ recipe_type: e.target.value })}
            >
              <option value="basis">{t('recipes.recipeTypeBasis')}</option>
              <option value="uitgebreid">{t('recipes.recipeTypeUitgebreid')}</option>
            </select>
          </div>
        </div>

        {form.recipe_type === 'uitgebreid' && (
          <>
            <div className="card" style={{ marginBottom: 16, padding: 20 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('recipes.labelPhotos')}</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  {photos.map((p, i) => (
                    <div key={p._id} style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                      <img src={getRecipePhotoUrl(p.storage_path)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {i === 0 && (
                        <span style={{ position: 'absolute', top: 4, left: 4, background: 'var(--color-primary)', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 6, padding: '2px 6px' }}>
                          {t('recipes.coverPhotoBadge')}
                        </span>
                      )}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', background: 'rgba(0,0,0,0.5)', padding: '2px 4px' }}>
                        <button type="button" onClick={() => movePhoto(i, -1)} disabled={i === 0} style={{ background: 'none', border: 'none', color: '#fff', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1 }} title={t('recipes.movePhotoUp')}>◀</button>
                        <button type="button" onClick={() => removePhoto(i)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }} title={t('recipes.removePhoto')}>✕</button>
                        <button type="button" onClick={() => movePhoto(i, 1)} disabled={i === photos.length - 1} style={{ background: 'none', border: 'none', color: '#fff', cursor: i === photos.length - 1 ? 'default' : 'pointer', opacity: i === photos.length - 1 ? 0.3 : 1 }} title={t('recipes.movePhotoDown')}>▶</button>
                      </div>
                    </div>
                  ))}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePickPhotos} disabled={uploadingPhoto} style={{ fontSize: 13 }} />
                {uploadingPhoto && <span className="text-muted" style={{ marginLeft: 8 }}>{t('recipes.uploadingPhoto')}</span>}
                {photos.length === 0 && !uploadingPhoto && <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>{t('recipes.noPhotos')}</p>}
              </div>
            </div>

            <div className="card" style={{ marginBottom: 16, padding: 20 }}>
              <div className="form-group">
                <label className="form-label">{t('recipes.labelVideoUrl')}</label>
                <input className="form-input" value={form.video_url} onChange={e => updateForm({ video_url: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('recipes.labelPrepTime')}</label>
                  <input className="form-input" type="number" min="0" value={form.prep_time_minutes} onChange={e => updateForm({ prep_time_minutes: e.target.value })} onWheel={blurOnWheel} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('recipes.labelCookTime')}</label>
                  <input className="form-input" type="number" min="0" value={form.cook_time_minutes} onChange={e => updateForm({ cook_time_minutes: e.target.value })} onWheel={blurOnWheel} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('recipes.labelServings')}</label>
                  <input className="form-input" type="number" min="0" value={form.servings} onChange={e => updateForm({ servings: e.target.value })} onWheel={blurOnWheel} />
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 16, marginBottom: 0 }}>
                <label className="form-label">{t('recipes.labelDifficulty')}</label>
                <select className="form-input" value={form.difficulty} onChange={e => updateForm({ difficulty: e.target.value })}>
                  {DIFFICULTIES.map(d => (
                    <option key={d} value={d}>{difficultyLabel(d)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="card" style={{ marginBottom: 16, padding: 20 }}>
              <label className="form-label">{t('recipes.labelIngredients')}</label>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleIngredientDragEnd}>
                <SortableContext items={ingredients.map(i => i._id)} strategy={verticalListSortingStrategy}>
                  {ingredients.map((ing, i) => (
                    <SortableIngredientRow key={ing._id} id={ing._id} ing={ing} index={i} onUpdate={updateIngredientRow} onRemove={removeIngredientRow} />
                  ))}
                </SortableContext>
              </DndContext>
              <button type="button" className="btn btn-ghost btn-sm" onClick={addIngredientRow}>{t('recipes.addIngredient')}</button>
            </div>

            <div className="card" style={{ marginBottom: 16, padding: 20 }}>
              <label className="form-label">{t('recipes.labelSteps')}</label>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
                <SortableContext items={steps.map(s => s._id)} strategy={verticalListSortingStrategy}>
                  {steps.map((s, i) => (
                    <SortableStepRow key={s._id} id={s._id} s={s} index={i} onUpdate={updateStepRow} onRemove={removeStepRow} />
                  ))}
                </SortableContext>
              </DndContext>
              <button type="button" className="btn btn-ghost btn-sm" onClick={addStepRow}>{t('recipes.addStep')}</button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20, marginBottom: 40 }}>
          <button type="button" className="btn btn-ghost" onClick={handleCancel}>{t('recipes.annuleren')}</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '…' : t('recipes.opslaan')}
          </button>
        </div>
      </form>

      {showCancelConfirm && (
        <ConfirmModal
          title={t('recipes.verlatenTitel')}
          body={t('recipes.verlatenTekst')}
          confirmLabel={t('recipes.verlatenBevestigen')}
          danger
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={() => { setShowCancelConfirm(false); navigate('/coach/recepten'); }}
        />
      )}
    </div>
  );
}
